import { useId, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { formatCentralTime } from '@/lib/formatCentralTime'

export interface DealNote { id: string; body: string; createdAt: string; updatedAt: string; authorName: string | null }
export interface DealNotesData { notes: DealNote[]; hasMore: boolean }
export interface DealNotesTransport {
  list: (dealId: string) => Promise<DealNotesData>
  add: (dealId: string, body: string) => Promise<DealNote>
  update: (dealId: string, noteId: string, body: string) => Promise<DealNote>
  remove: (dealId: string, noteId: string) => Promise<void>
}
const base = (id: string) => `/api/deals/${encodeURIComponent(id)}/notes`
const liveTransport: DealNotesTransport = {
  list: async (id) => (await apiClient.get<{ data: DealNotesData }>(base(id))).data.data,
  add: async (id, body) => (await apiClient.post<{ data: DealNote }>(base(id), { body })).data.data,
  update: async (id, nid, body) => (await apiClient.patch<{ data: DealNote }>(`${base(id)}/${encodeURIComponent(nid)}`, { body })).data.data,
  remove: async (id, nid) => { await apiClient.delete(`${base(id)}/${encodeURIComponent(nid)}`) },
}

/** Newest first; ties broken by id desc (matches the server order). */
export function sortNotesNewestFirst(notes: DealNote[]): DealNote[] {
  return [...notes].sort((a, b) => {
    const d = Date.parse(b.createdAt) - Date.parse(a.createdAt)
    return d !== 0 ? d : b.id < a.id ? -1 : b.id > a.id ? 1 : 0
  })
}

export function DealNotes({ dealId, canEdit, transport = liveTransport }: {
  dealId: string; canEdit: boolean; transport?: DealNotesTransport
}) {
  return <Notes key={dealId} dealId={dealId} canEdit={canEdit} transport={transport} />
}

function Notes({ dealId, canEdit, transport }: { dealId: string; canEdit: boolean; transport: DealNotesTransport }) {
  const client = useQueryClient()
  const labelId = useId()
  const inputId = useId()
  const [draft, setDraft] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState('')
  const queryKey = ['deal-notes', dealId]
  const query = useQuery({ queryKey, queryFn: () => transport.list(dealId), retry: false })
  const refresh = () => void client.invalidateQueries({ queryKey })

  const add = useMutation({
    mutationFn: (body: string) => transport.add(dealId, body),
    retry: false,
    onSuccess: (note) => {
      client.setQueryData<DealNotesData>(queryKey, (old) => ({
        notes: sortNotesNewestFirst([note, ...(old?.notes ?? []).filter((n) => n.id !== note.id)]),
        hasMore: Boolean(old?.hasMore),
      }))
      setDraft('')
      refresh()
    },
  })
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) => transport.update(dealId, id, body),
    retry: false,
    onSuccess: (note) => {
      client.setQueryData<DealNotesData>(queryKey, (old) => old && ({ ...old, notes: old.notes.map((n) => n.id === note.id ? note : n) }))
      setEditingId(null)
      refresh()
    },
  })
  const remove = useMutation({
    mutationFn: (id: string) => transport.remove(dealId, id),
    retry: false,
    onSuccess: (_v, id) => {
      client.setQueryData<DealNotesData>(queryKey, (old) => old && ({ ...old, notes: old.notes.filter((n) => n.id !== id) }))
      refresh()
    },
  })

  const trimmed = draft.trim()
  const submit = () => { if (trimmed && !add.isPending) add.mutate(trimmed) }

  return <section className="ds-tile-inset p-4 space-y-3" aria-labelledby={labelId} data-testid="deal-notes">
    <h3 id={labelId} className="font-semibold">Notes</h3>
    {canEdit && <form data-testid="deal-note-form" className="space-y-2" onSubmit={(e) => { e.preventDefault(); submit() }}>
      <label htmlFor={inputId} className="sr-only">New note</label>
      <textarea id={inputId} value={draft} maxLength={10000} rows={3} placeholder="Add a note…"
        className="w-full rounded border border-[var(--border)] bg-transparent p-2"
        onChange={(e) => setDraft(e.target.value)} />
      {add.isError && <p role="alert">Note could not be saved. Your text is still here.</p>}
      <Button type="submit" size="sm" disabled={!trimmed || add.isPending} loading={add.isPending}>Add note</Button>
    </form>}
    {query.isPending ? <p role="status">Loading notes…</p> : query.isError ? (
      <div role="alert"><p>Notes could not be loaded.</p>
        <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>Retry notes</Button></div>
    ) : query.data.notes.length === 0 ? <p className="text-sm text-midnight-text3">No notes yet.</p> : (
      <ul className="space-y-3" aria-label="Lead notes" data-testid="deal-notes-list">
        {sortNotesNewestFirst(query.data.notes).map((note) => {
          const edited = Date.parse(note.updatedAt) - Date.parse(note.createdAt) > 1000
          return <li key={note.id} className="border-b border-[var(--border)] pb-3">
            <p className="text-sm text-midnight-text3">
              <time dateTime={note.createdAt}>{formatCentralTime(note.createdAt)}</time>
              {note.authorName ? ` · ${note.authorName}` : ''}{edited ? ' · edited' : ''}
            </p>
            {editingId === note.id ? <div className="space-y-2 mt-1">
              <label className="sr-only" htmlFor={`edit-${note.id}`}>Edit note</label>
              <textarea id={`edit-${note.id}`} value={editDraft} maxLength={10000} rows={3}
                className="w-full rounded border border-[var(--border)] bg-transparent p-2"
                onChange={(e) => setEditDraft(e.target.value)} />
              {update.isError && <p role="alert">Edit could not be saved.</p>}
              <div className="flex gap-2">
                <Button size="sm" disabled={!editDraft.trim() || update.isPending}
                  onClick={() => update.mutate({ id: note.id, body: editDraft.trim() })}>Save</Button>
                <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>Cancel</Button>
              </div>
            </div> : <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] mt-1">{note.body}</p>}
            {canEdit && editingId !== note.id && <div className="flex gap-2 mt-1">
              <Button size="sm" variant="secondary" onClick={() => { setEditingId(note.id); setEditDraft(note.body) }}>Edit</Button>
              <Button size="sm" variant="secondary" disabled={remove.isPending}
                onClick={() => { if (window.confirm('Delete this note?')) remove.mutate(note.id) }}>Delete</Button>
            </div>}
          </li>
        })}
      </ul>
    )}
  </section>
}
